/* SSi website – shared Legal document template.
 *
 * Used by privacy.html, terms.html, refunds.html. Deliberately
 * restrained: a content-width column, display-font hero title,
 * "last updated" + "read time" line, a sticky section-nav rail
 * on the left, and simple prose sections on the right.
 *
 * Content shape expected in `t`:
 *   legalKicker        – e.g. "Legal"
 *   legalTitle         – page title
 *   legalLede          – one-liner about the document
 *   legalLastUpdated   – "Last updated: 14 April 2026"
 *   legalReadTime      – "~6 min read"
 *   legalIntro         – introductory paragraph (optional)
 *   legalSections      – array of { id, heading, body: string[] }
 *                       (passed directly, not through T keys, to
 *                        keep the per-page file readable)
 *   legalContactNote   – footer line ("Questions? …")
 *
 * `sections` is passed as a prop (not via t) because we want rich,
 * multi-paragraph content that doesn't flatten well into tweak keys.
 * The top-level strings above ARE tweakable so the user can edit
 * the hero and framing without diving into JSX.
 */

import { Link } from 'react-router-dom';

export function LegalDoc({ t, sections }) {
  return (
    <article className="ssi-legal" style={legalStyles.wrap}>
      {/* Hero */}
      <header className="ssi-legal__hero" style={legalStyles.hero}>
        <div style={legalStyles.heroInner}>
          <div style={legalStyles.kicker}>{t.legalKicker}</div>
          <h1 style={legalStyles.h1}>{t.legalTitle}</h1>
          {t.legalLede && <p style={legalStyles.lede}>{t.legalLede}</p>}
          <div style={legalStyles.meta}>
            {t.legalLastUpdated && <span>{t.legalLastUpdated}</span>}
            {t.legalLastUpdated && t.legalReadTime && <span style={legalStyles.metaDot}>·</span>}
            {t.legalReadTime && <span>{t.legalReadTime}</span>}
          </div>
        </div>
      </header>

      {/* Body: two-column with sticky section nav */}
      <div className="ssi-legal__body" style={legalStyles.body}>
        <nav className="ssi-legal__nav" style={legalStyles.nav} aria-label="On this page">
          <div style={legalStyles.navHead}>On this page</div>
          <ol style={legalStyles.navList}>
            {sections.map((s, i) => (
              <li key={s.id} style={legalStyles.navItem}>
                <a href={`#${s.id}`} style={legalStyles.navLink}>
                  <span style={legalStyles.navNum}>{String(i + 1).padStart(2, '0')}</span>
                  <span>{s.heading}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="ssi-legal__content" style={legalStyles.content}>
          {t.legalIntro && <p style={legalStyles.intro}>{t.legalIntro}</p>}

          {sections.map((s, i) => (
            <section key={s.id} id={s.id} style={legalStyles.section}>
              <div style={legalStyles.sectionNum}>{String(i + 1).padStart(2, '0')}</div>
              <h2 style={legalStyles.h2}>{s.heading}</h2>
              {s.body.map((para, j) => {
                // Allow an item that is itself a list: array of bullets
                if (Array.isArray(para)) {
                  return (
                    <ul key={j} style={legalStyles.ul}>
                      {para.map((item, k) => <li key={k} style={legalStyles.li}>{item}</li>)}
                    </ul>
                  );
                }
                return <p key={j} style={legalStyles.p}>{para}</p>;
              })}
            </section>
          ))}

          {t.legalContactNote && (
            <aside style={legalStyles.contact}>
              <div style={legalStyles.contactKicker}>Questions?</div>
              <p style={legalStyles.contactBody}>{t.legalContactNote}</p>
              <Link to="/contact" className="btn btn--ghost" style={{border:'1px solid var(--border-strong)'}}>Get in touch</Link>
            </aside>
          )}
        </div>
      </div>
    </article>
  );
}

const legalStyles = {
  wrap: { background:'#fff' },

  /* hero */
  hero: { background:'var(--offWhite, #FDF5F5)', padding:'80px 32px 72px', borderBottom:'1px solid var(--border)' },
  heroInner: { maxWidth:1080, margin:'0 auto' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:18 },
  h1: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:56, lineHeight:1.05, letterSpacing:'-.02em', margin:'0 0 18px', textWrap:'balance', maxWidth:'20ch' },
  lede: { fontFamily:'var(--font-body)', fontSize:19, lineHeight:1.55, color:'var(--grey)', margin:'0 0 22px', maxWidth:'58ch', textWrap:'pretty' },
  meta: { fontSize:13, color:'var(--grey)', letterSpacing:'.01em', display:'flex', gap:10, alignItems:'center' },
  metaDot: { opacity:.5 },

  /* body */
  body: { maxWidth:1240, margin:'0 auto', padding:'64px 32px 120px', display:'grid', gridTemplateColumns:'240px 1fr', gap:72, alignItems:'start' },

  nav: { position:'sticky', top:28, alignSelf:'start', paddingTop:6 },
  navHead: { fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--grey)', fontWeight:700, marginBottom:16 },
  navList: { listStyle:'none', margin:0, padding:0, display:'flex', flexDirection:'column', gap:2, borderLeft:'1px solid var(--border)' },
  navItem: {},
  navLink: {
    display:'flex', gap:12, alignItems:'baseline',
    padding:'10px 14px', marginLeft:-1,
    textDecoration:'none', color:'var(--fg-1)',
    fontSize:14.5, lineHeight:1.35,
    borderLeft:'1px solid transparent',
    transition:'color 120ms, border-color 120ms, background 120ms',
  },
  navNum: { fontSize:11, color:'var(--grey)', fontWeight:600, fontVariantNumeric:'tabular-nums' },

  content: { maxWidth:720 },
  intro: {
    fontSize:18, lineHeight:1.6, color:'var(--fg-1)', margin:'0 0 48px',
    paddingBottom:32, borderBottom:'1px solid var(--border)', textWrap:'pretty',
  },

  section: { marginBottom:48, scrollMarginTop:28 },
  sectionNum: {
    fontSize:11, letterSpacing:'.14em', textTransform:'uppercase',
    color:'var(--brandRed)', fontWeight:700, marginBottom:10,
    fontVariantNumeric:'tabular-nums',
  },
  h2: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:30, lineHeight:1.15, letterSpacing:'-.01em', margin:'0 0 16px', textWrap:'balance' },
  p: { fontSize:16, lineHeight:1.65, color:'var(--fg-1)', margin:'0 0 16px', textWrap:'pretty' },
  ul: { margin:'0 0 16px', paddingLeft:20, display:'flex', flexDirection:'column', gap:8 },
  li: { fontSize:16, lineHeight:1.6, color:'var(--fg-1)', textWrap:'pretty' },

  contact: {
    background:'var(--offWhite, #FDF5F5)', border:'1px solid var(--border)',
    borderRadius:18, padding:'28px 32px', marginTop:56,
  },
  contactKicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:8 },
  contactBody: { fontSize:15.5, lineHeight:1.55, color:'var(--fg-1)', margin:'0 0 18px', textWrap:'pretty' },
};
